import { useMemo } from "react";
import { ctrlRegistry } from "../common/components/CustomCtrl";
import { doRegistration as doCommonRegistration } from "../common/components/customCtrlRegistrations";
import { ensureArray } from "../common/utils/arrayUtilities";
import { now } from "../common/utils/CDate";
import MemberLastRoster from "./memberEditor/MemberLastRoster";

export const doRegistration = () => {
    doCommonRegistration();
    ctrlRegistry.register('brand', () => import('./BrandDropdown'),
        (BrandDropdown, { ...props }) => <BrandDropdown {...props}></BrandDropdown>
    );
    ctrlRegistry.register('entity', () => import('./EntityDropdown'),
        (EntityDropdown, { ...props }) => <EntityDropdown {...props}></EntityDropdown>
    );
    ctrlRegistry.register('featuresinput', () => import('./FeaturesInput'),
        (FeaturesInput, { value, ...props }) => <FeaturesInput features={value} {...props}></FeaturesInput>
    );
    ctrlRegistry.register('client', () => import('./ClientInput'),
        (ClientInput, { value, def, ...props }, { args }) => <ClientInput client={value} disableEditor={def.disableEditor} location={args?.location ?? def.location} office={args?.office ?? def.office} includeReadonly={args?.includeReadonly ?? def.includeReadonly} exclude={def.exclude} {...props}></ClientInput>
    );
    ctrlRegistry.register('location', () => import('./LocationInput'),
        (LocationInput, { value, def, onChange, ...props }, { args }) => <LocationInput location={value} disableEditor={def.disableEditor} onChange={(_, value) => onChange(value)} client={args?.client ?? def.client} includeReadonly={args?.includeReadonly ?? def.includeReadonly} exclude={def.exclude} {...props}></LocationInput>
    );
    ctrlRegistry.register('locationWithPosts', () => import('./LocationInput'),
        (LocationInput, { value, def, ...props }, { args }) => <LocationInput location={value?.location} disableEditor={def.disableEditor} client={args?.client ?? def.client} post={value?.post} showPosts={args?.showPosts ?? def.showPosts} includeReadonly={args?.includeReadonly ?? def.includeReadonly} {...props}></LocationInput>
    );
    ctrlRegistry.register('qualification', () => import('./QualificationInput'),
    (QualificationInput, { value, def, ...props }) => <QualificationInput qualificationid={value} exclude={def?.exclude} {...props}></QualificationInput>,
    );
    ctrlRegistry.register('member', () => import('./MemberInput'),
        (MemberInput, { value, def, ...props}, { args }) => <MemberInput member={value} exclude={args?.exclude ?? def.exclude} {...props}></MemberInput>
    );
    ctrlRegistry.register('members', () => import('./MemberTagManager'),
        (MemberTagManager, { value, def, ...props }) => <MemberTagManager value={value} labelField={def.labelField} {...props}></MemberTagManager>
    );
    ctrlRegistry.register('award', () => import('./AwardDropdown'),
        (AwardDropdown, { ...props}) => <AwardDropdown {...props}></AwardDropdown>
    );
    ctrlRegistry.register('visa', () => import('./VisaDropdown'),
        (VisaDropdown, { ...props }) => <VisaDropdown {...props}></VisaDropdown>
    );
    ctrlRegistry.register('office', () => import('./Offices'),
        (Office, { def, ...props }, { args }) => <Office client={args?.client ?? def.client} location={args?.location ?? def.location} includeReadonly={args?.includeReadonly ?? def.includeReadonly ?? true} {...props}></Office>
    );
    ctrlRegistry.register('officeDropdown', () => import('./OfficeDropdown'),
        (OfficeDropdown, { def, ...props }, { args }) => <OfficeDropdown client={args?.client ?? def.client} location={args?.location ?? def.location} includeReadonly={args?.includeReadonly ?? def.includeReadonly ?? true} {...props}></OfficeDropdown>
    );
    ctrlRegistry.register('coreAccreditation', async () => {
            const AccreditationInput = await import('./AccreditationInput');
            return { 
                default: AccreditationInput.CoreAccreditationInput,
                toString: AccreditationInput.toString
            }
        },
        (CoreAccreditationInput, {value, ...props}) => {
            const displayValue = useMemo(() => ensureArray(value), [value]);
            return <CoreAccreditationInput value={displayValue} {...props}></CoreAccreditationInput>
        }
    );
    ctrlRegistry.register('supplementalAccreditation', async () => {
            const AccreditationInput = await import('./AccreditationInput');
            return { 
                default: AccreditationInput.SupplementalAccreditationInput,
                toString: AccreditationInput.toString
            }
        },
        (SupplementalAccreditationInput, {...props}) => <SupplementalAccreditationInput {...props}></SupplementalAccreditationInput>
    );
    ctrlRegistry.register('memberAccreditation', () => import('./MemberAccreditationLabel'),
        (MemberAccreditationLabel, {value, def}, {args}) => <MemberAccreditationLabel memberid={value} date={args?.date ?? def.date ?? now().toISOString()} shortFormat={def?.shortFormat}></MemberAccreditationLabel>
    );
    ctrlRegistry.register('groupAccreditation', () => import('./rostering/GroupAccreditationLabel'),
    (GroupAccreditationLabel, {value, def}, {args}) => <GroupAccreditationLabel groupid={value} format={args?.format ?? def?.format}></GroupAccreditationLabel>
    );
    ctrlRegistry.register('itemName', async () => {
        const ItemTypeahead = await import('./equipmentEditor/ItemTypeahead');
        return { 
            default: ItemTypeahead.ItemNameTypeahead,
            toString: ItemTypeahead.itemNametoString
            }
        },
    (ItemNameTypeahead, {value, def, ...props}) => <ItemNameTypeahead disableEditor={def.disableEditor} item={value} {...props}></ItemNameTypeahead>
    );
    ctrlRegistry.register('itemCode', async () => {
        const ItemTypeahead = await import('./equipmentEditor/ItemTypeahead');
        return { 
            default: ItemTypeahead.ItemCodeTypeahead,
            toString: ItemTypeahead.itemCodetoString
        }
    },
    (ItemCodeTypeahead, {value, def, ...props}) => <ItemCodeTypeahead item={value} disableEditor={def.disableEditor} {...props}></ItemCodeTypeahead>
    );
    ctrlRegistry.register('supplier', () => import('./supplierEditor/SupplierInput'),
        (SupplierInput, { value, def, ...props }) => <SupplierInput supplier={value} disableEditor={def.disableEditor} {...props}></SupplierInput>
    );
    ctrlRegistry.register('rosterRole', () => import('./RosterRoleDropdown'), 
        (RosterRoleDropdown, {...props }) => <RosterRoleDropdown {...props}></RosterRoleDropdown>
    );
    ctrlRegistry.register('hoursClaimType', () => import('./HoursClaimTypeDropdown'),
        (HoursClaimTypeDropdown, { ...props }) => <HoursClaimTypeDropdown {...props}></HoursClaimTypeDropdown>
    );
    ctrlRegistry.register('memberLastRoster', () => import('./memberEditor/MemberLastRoster'),
        (MemberLastRoster, { value, tz, ...props }) => {
            return <MemberLastRoster memberid={value} tz={tz}></MemberLastRoster>
        }
    );
}